import { Dialog, Flex, Notice, Text, Button, useToasts } from '@applyboard/crystal-ui'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FlywireButton } from '../../../components/Payments'
import { StudentApplication } from '../../../components/Application/types'
import { useBypassPaymentAndSubmit } from '../../../hooks/applications/useBypassPaymentAndSubmit'

export function PayAndSubmitApprovalDialog(props: {
  showSubmitApprovalDialog: boolean
  setShowSubmitApprovalDialog: Dispatch<SetStateAction<boolean>>
  application: StudentApplication
  onBypassPaymentAndSubmit?: () => void
  showBypassButton?: boolean
}) {
  const { showSubmitApprovalDialog, setShowSubmitApprovalDialog, application, onBypassPaymentAndSubmit } = props
  
  const queryClient = useQueryClient()
  const { isLoading, bypassPaymentAndSubmit, isSuccess, isError, error } = useBypassPaymentAndSubmit({
    id: application.id,
  })
  const toast = useToasts()

  useEffect(() => {
    if (isSuccess && showSubmitApprovalDialog) {
      toast.positive('Application submitted successfully with bypassed payment')
      queryClient.invalidateQueries({ queryKey: ['applications', application.id] })
      setShowSubmitApprovalDialog(false)
      if (onBypassPaymentAndSubmit) {
        onBypassPaymentAndSubmit()
      }
    }
  }, [isSuccess, showSubmitApprovalDialog, setShowSubmitApprovalDialog, onBypassPaymentAndSubmit, toast, queryClient, application.id])

  useEffect(() => {
    if (isError && showSubmitApprovalDialog) {
      const errorMessage = error?.message || 'Failed to bypass payment and submit application'
      toast.negative(new Error(errorMessage))
    }
  }, [isError, error, showSubmitApprovalDialog, toast])

  const handleBypassClick = () => {
    bypassPaymentAndSubmit({})
  }

  if (!showSubmitApprovalDialog) {
    return null
  }

  return (
    <Dialog
      onOpenChange={open => {
        setShowSubmitApprovalDialog(open)
      }}
      open={showSubmitApprovalDialog}
    >
      <Dialog.Heading>Ready to submit?</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" justify="center" align="center" gap={4}>
          <Notice actionText="" intent="primary" width="fill">
            Please note that you will not be able to make any changes to this application after
            submission.
          </Notice>
          <Text variant="bodyM">Well done! This application is ready for submission.</Text>
          <Text variant="bodyM" contrast="low" align="center">
            But first, you will need to pay the non-refundable application fee. Once your payment is
            processed, this application will automatically be submitted.
          </Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        
        {props.showBypassButton && (
          <Button
            onClick={handleBypassClick}
            loading={isLoading}
            width="fill"
            intent="primary"
          >
            Bypass payment
          </Button>
        )}
        
        <FlywireButton
          onClick={() => {
            setShowSubmitApprovalDialog(false)
          }}
          application={application}
        />
      </Dialog.Actions>
    </Dialog>
  )
}
